/**
 * Tactical Board Service
 *
 * Handles all tactical board API calls including:
 * - CRUD for reusable tactical diagrams
 * - Linking boards to training plans
 * - Linking boards to RTP exercises
 */

import { HTTP } from "./http";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER = "http://www.localhost:8888/back-end-endpoint-tactical-board.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-tactical-board.php";
}

const TacticalBoardService = {
  /**
   * Get all tactical boards for a club
   * @param {object} data - { clubId, category (optional), include_inactive (optional) }
   */
  getTacticalBoards(data) {
    return HTTP.post(API_SERVER, {
      action: 'getTacticalBoards',
      ...data
    });
  },

  /**
   * Get a single tactical board by ID
   * @param {number} boardId
   */
  getTacticalBoard(boardId) {
    return HTTP.post(API_SERVER, {
      action: 'getTacticalBoard',
      board_id: boardId
    });
  },

  /**
   * Save a new tactical board
   * @param {object} data - { clubId, title, description, category, elements, background, background_image, orientation, image_transform, created_by }
   */
  saveTacticalBoard(data) {
    return HTTP.post(API_SERVER, {
      action: 'saveTacticalBoard',
      ...data
    });
  },

  /**
   * Update an existing tactical board
   * @param {number} boardId
   * @param {object} data - fields to update
   */
  updateTacticalBoard(boardId, data) {
    return HTTP.post(API_SERVER, {
      action: 'updateTacticalBoard',
      board_id: boardId,
      ...data
    });
  },

  /**
   * Soft-delete a tactical board
   * @param {number} boardId
   */
  deleteTacticalBoard(boardId) {
    return HTTP.post(API_SERVER, {
      action: 'deleteTacticalBoard',
      board_id: boardId
    });
  },

  /**
   * Duplicate a tactical board
   * @param {number} boardId
   * @param {number} createdBy
   */
  duplicateTacticalBoard(boardId, createdBy) {
    return HTTP.post(API_SERVER, {
      action: 'duplicateTacticalBoard',
      board_id: boardId,
      created_by: createdBy
    });
  },

  // ---- Training Plan Linking ----

  /**
   * Link a board to a training plan
   * @param {object} data - { board_id, training_plan_id, sort_order, custom_notes }
   */
  linkBoardToTrainingPlan(data) {
    return HTTP.post(API_SERVER, {
      action: 'linkBoardToTrainingPlan',
      ...data
    });
  },

  /**
   * Unlink a board from a training plan
   * @param {number} boardId
   * @param {number} planId
   */
  unlinkBoardFromTrainingPlan(boardId, planId) {
    return HTTP.post(API_SERVER, {
      action: 'unlinkBoardFromTrainingPlan',
      board_id: boardId,
      training_plan_id: planId
    });
  },

  /**
   * Get all boards linked to a training plan
   * @param {number} planId
   */
  getBoardsForTrainingPlan(planId) {
    return HTTP.post(API_SERVER, {
      action: 'getBoardsForTrainingPlan',
      training_plan_id: planId
    });
  },

  /**
   * Get the first linked board for each of the given training plans
   * (used for card thumbnails)
   * @param {number[]} planIds
   */
  getFirstBoardForTrainingPlans(planIds) {
    return HTTP.post(API_SERVER, {
      action: 'getFirstBoardForTrainingPlans',
      training_plan_ids: planIds
    });
  },

  // ---- RTP Exercise Linking ----

  /**
   * Link a board to an RTP exercise
   * @param {object} data - { board_id, exercise_id, sort_order, custom_notes }
   */
  linkBoardToExercise(data) {
    return HTTP.post(API_SERVER, {
      action: 'linkBoardToExercise',
      ...data
    });
  },

  /**
   * Unlink a board from an RTP exercise
   * @param {number} boardId
   * @param {number} exerciseId
   */
  unlinkBoardFromExercise(boardId, exerciseId) {
    return HTTP.post(API_SERVER, {
      action: 'unlinkBoardFromExercise',
      board_id: boardId,
      exercise_id: exerciseId
    });
  },

  /**
   * Get all boards linked to an RTP exercise
   * @param {number} exerciseId
   */
  getBoardsForExercise(exerciseId) {
    return HTTP.post(API_SERVER, {
      action: 'getBoardsForExercise',
      exercise_id: exerciseId
    });
  }
};

export { TacticalBoardService };
